export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface ScheduleWindow {
  days: Weekday[];
  start: string;
  end: string;
}

export interface HolidayOverride {
  date: string;
  mode: 'closed' | 'open';
  windows?: ScheduleWindow[];
}

export interface HolidayPreset {
  id: string;
  label: string;
  date: string;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function utcDate(year: number, monthIndex: number, day: number): Date {
  return new Date(Date.UTC(year, monthIndex, day));
}

function observedFixedDate(year: number, monthIndex: number, day: number): string {
  const date = utcDate(year, monthIndex, day);
  const weekday = date.getUTCDay();
  if (weekday === 6) date.setUTCDate(date.getUTCDate() - 1);
  if (weekday === 0) date.setUTCDate(date.getUTCDate() + 1);
  return isoDate(date);
}

function nthWeekdayOfMonth(
  year: number,
  monthIndex: number,
  weekday: number,
  nth: number,
): string {
  const date = utcDate(year, monthIndex, 1);
  const delta = (weekday - date.getUTCDay() + 7) % 7;
  date.setUTCDate(1 + delta + (nth - 1) * 7);
  return isoDate(date);
}

function lastWeekdayOfMonth(year: number, monthIndex: number, weekday: number): string {
  const date = utcDate(year, monthIndex + 1, 0);
  const delta = (date.getUTCDay() - weekday + 7) % 7;
  date.setUTCDate(date.getUTCDate() - delta);
  return isoDate(date);
}

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return isoDate(d);
}

export function commonHolidayPresets(year: number): HolidayPreset[] {
  const thanksgiving = nthWeekdayOfMonth(year, 10, 4, 4);
  return [
    { id: 'new-years-day', label: "New Year's Day", date: observedFixedDate(year, 0, 1) },
    {
      id: 'mlk-day',
      label: 'Martin Luther King Jr. Day',
      date: nthWeekdayOfMonth(year, 0, 1, 3),
    },
    {
      id: 'presidents-day',
      label: "Presidents' Day",
      date: nthWeekdayOfMonth(year, 1, 1, 3),
    },
    {
      id: 'memorial-day',
      label: 'Memorial Day',
      date: lastWeekdayOfMonth(year, 4, 1),
    },
    { id: 'juneteenth', label: 'Juneteenth', date: observedFixedDate(year, 5, 19) },
    {
      id: 'independence-day',
      label: 'Independence Day',
      date: observedFixedDate(year, 6, 4),
    },
    { id: 'labor-day', label: 'Labor Day', date: nthWeekdayOfMonth(year, 8, 1, 1) },
    {
      id: 'columbus-indigenous-day',
      label: 'Columbus Day / Indigenous Peoples Day',
      date: nthWeekdayOfMonth(year, 9, 1, 2),
    },
    { id: 'veterans-day', label: 'Veterans Day', date: observedFixedDate(year, 10, 11) },
    { id: 'thanksgiving', label: 'Thanksgiving Day', date: thanksgiving },
    {
      id: 'day-after-thanksgiving',
      label: 'Day after Thanksgiving',
      date: addDays(thanksgiving, 1),
    },
    { id: 'christmas-eve', label: 'Christmas Eve', date: observedFixedDate(year, 11, 24) },
    { id: 'christmas-day', label: 'Christmas Day', date: observedFixedDate(year, 11, 25) },
    { id: 'new-years-eve', label: "New Year's Eve", date: observedFixedDate(year, 11, 31) },
  ];
}

export function mergeClosedHolidayOverrides(
  existing: HolidayOverride[],
  dates: string[],
): HolidayOverride[] {
  const seen = new Set(existing.map((override) => override.date));
  const additions = [...new Set(dates)]
    .filter((date) => !seen.has(date))
    .sort()
    .map((date) => ({ date, mode: 'closed' as const }));
  return [...existing, ...additions];
}

export function removeHolidayOverride(existing: HolidayOverride[], date: string): HolidayOverride[] {
  return existing.filter((override) => override.date !== date);
}
